import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Droplets,
  AlertCircle,
  Save,
  Loader2
} from 'lucide-react';

const ProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    address: user?.address || '',
    blood_group: user?.blood_group || '',
    allergies: user?.allergies?.join(', ') || '',
    medical_conditions: user?.medical_conditions?.join(', ') || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const updates = {
      ...formData,
      allergies: formData.allergies.split(',').map(a => a.trim()).filter(Boolean),
      medical_conditions: formData.medical_conditions.split(',').map(m => m.trim()).filter(Boolean)
    };

    const result = await updateProfile(updates);
    setLoading(false);

    if (result.success) {
      toast.success('Profile updated successfully');
    } else {
      toast.error(result.error);
    }
  };

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="profile-page">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your personal information and medical details</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card-clinical">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-sky-600" />
              Basic Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  className="mt-1"
                  placeholder="Enter your full name"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="mt-1 bg-slate-50 dark:bg-slate-800"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="mt-1"
                  placeholder="+234 XXX XXX XXXX"
                  data-testid="profile-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleChange('date_of_birth', e.target.value)}
                  className="mt-1"
                  data-testid="profile-dob-input"
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender</Label>
                <Select 
                  value={formData.gender || undefined} 
                  onValueChange={(v) => handleChange('gender', v)}
                >
                  <SelectTrigger className="mt-1" data-testid="profile-gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="blood_group">Blood Group</Label>
                <Select 
                  value={formData.blood_group || undefined} 
                  onValueChange={(v) => handleChange('blood_group', v)}
                >
                  <SelectTrigger className="mt-1" data-testid="profile-blood-group-select">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    {bloodGroups.map((bg) => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Enter your full address"
                className="mt-1"
                data-testid="profile-address-input"
              />
            </div>
          </div>

          {/* Medical Information */}
          <div className="card-clinical">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-sky-600" />
              Medical Information
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="allergies">Known Allergies</Label>
                <Textarea
                  id="allergies"
                  value={formData.allergies}
                  onChange={(e) => handleChange('allergies', e.target.value)}
                  placeholder="Enter allergies separated by commas (e.g., Penicillin, Peanuts)"
                  className="mt-1"
                  data-testid="profile-allergies-input"
                />
              </div>
              <div>
                <Label htmlFor="conditions">Medical Conditions</Label>
                <Textarea
                  id="conditions"
                  value={formData.medical_conditions}
                  onChange={(e) => handleChange('medical_conditions', e.target.value)}
                  placeholder="Enter medical conditions separated by commas (e.g., Diabetes, Hypertension)"
                  className="mt-1"
                  data-testid="profile-conditions-input"
                />
              </div>
            </div>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                This information helps us provide better care. Please keep it updated.
              </p>
            </div>
          </div>

          {/* Account Info */}
          <div className="card-clinical">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Account Information</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Account Type</span>
                <span className="font-medium text-slate-900 capitalize">{user?.role?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Member Since</span>
                <span className="font-medium text-slate-900">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-600">Account Status</span>
                <span className="badge-success">Active</span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              className="bg-sky-600 hover:bg-sky-700"
              disabled={loading}
              data-testid="profile-save-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
